import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { LoggerService } from '@app/logger/logger.service';
import { StockEquipmentsService } from '@app/shared/services/entities/stockEquipments.service';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '@app/material.module';
import { AutoSelectComponent } from '@app/shared/components/auto-select/auto-select.component';
const log = new LoggerService('EQUIPMENTS FORM COMPONENT');

import { rfidTagValidators } from '@app/shared/utils/rfid';
@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    AutoSelectComponent,
  ],
  selector: 'app-inventoryEquipments-form',
  templateUrl: './inventoryEquipments-form.component.html',
  styleUrls: ['./inventoryEquipments-form.component.scss']
})
export class InventoryEquipmentsFormComponent implements OnInit {

  public formGroup: FormGroup;
  public activeContract = null;
  public record = null;
  public equipmentList = [];
  /** Etiqueta del select de equipo: "TORRE 1 · ROMA". */
  readonly equipmentLabelFn = (item: any) => this.equipmentLabel(item);
  /** Sitios activos del catálogo, para el campo Ubicación. */
  public placeList: any[] = [];
  public readonly ownershipOptions = [
    { id: 'PROPIEDAD', name: 'Propiedad' },
    { id: 'COMODATO', name: 'Comodato' },
    { id: 'ARRENDAMIENTO', name: 'Arrendamiento' },
    { id: 'DEMO', name: 'Demo' },
    { id: 'PRESTAMO', name: 'Préstamo' },
  ];


  constructor(
    public dialogRef: MatDialogRef<InventoryEquipmentsFormComponent>,
    private customMessageService: CustomMessageService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private inventoryEquipmentsService: StockEquipmentsService
  ) { }

  async ngOnInit() {
    
    this.activeContract = this.data.contract;
    this.record = this.data.record;
    // Solo los equipos del hospital elegido (el catálogo nuevo trae `place`).
    const place = this.data.place ?? this.record?.location ?? null;
    this.equipmentList = ((this.data.equipments ?? []) as any[])
      .filter((e: any) => e?.id === this.record?.equipment || ((!place || e?.place === place) && e?.active !== false));
    this.placeList = (this.data.places ?? []).filter((p: any) => p?.active !== false);

    this.formGroup = this.formBuilder.group({
      // Se guarda el id del equipo (el select trabaja con ids).
      equipment: [this.record?.equipment ?? null, Validators.required],
      serialNumber: [this.record ? this.record.serialNumber : null, Validators.required],
      manufacturerYear: [this.record ? this.record.manufacturerYear : null, Validators.required],
      batch: [this.record?.batch ?? null],
      ownershipType: [this.record?.ownershipType ?? null],
      location: [this.record?.location ?? this.data.place ?? null],
      locationResponsible: [this.record?.locationResponsible ?? null],
      warrantyNumber: [this.record?.warrantyNumber ?? null],
      warrantyStart: [this.parseDate(this.record?.warrantyStart)],
      warrantyEnd: [this.parseDate(this.record?.warrantyEnd)],
      tagRfid: [this.record ? this.record.tagRfid : null, rfidTagValidators(true)],
      // El equipo ya no se liga a un locker: se conserva el valor viejo si lo tenía.
      warehouse: [this.record?.warehouse ?? null],
      type: [this.record ? this.record.type : this.data.equipmentType, Validators.required]
    });

    // Al elegir el equipo, la ubicación es la del equipo en el catálogo.
    this.formGroup.controls['equipment'].valueChanges.subscribe(id => {
      const eq = this.equipmentList.find((e: any) => e.id === id);
      if (eq?.place && !this.formGroup.value['location']) this.formGroup.controls['location'].setValue(eq.place);
    });

    if(this.data.equipmentType == 'Maleta'){
      let today = new Date();
      today.setHours(today.getHours() + 18);
      let date = today.toJSON().split('.')[0];
      ['-', '-', 'T', ':', ':'].forEach(elem => { date = date.replace(elem, ''); });
      let tag = '';
      tag = `ML00000000${date}`;
      this.formGroup.controls['tagRfid'].setValue(tag);
    }
    log.debug(this.data)

  }

  /** 'YYYY-MM-DD' -> Date local (sin corrimiento por zona horaria). */
  private parseDate(value: any): Date | null {
    if (!value) return null;
    const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
    return y && m && d ? new Date(y, m - 1, d) : null;
  }

  /** Date -> 'YYYY-MM-DD' con la fecha local que eligió el usuario. */
  private formatDate(value: any): string | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return null;
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${mm}-${dd}`;
  }

  /** Aplica el formato de fecha a los campos de garantía del payload. */
  private normalizeDates(data: any): any {
    data['warrantyStart'] = this.formatDate(this.formGroup.value['warrantyStart']);
    data['warrantyEnd'] = this.formatDate(this.formGroup.value['warrantyEnd']);
    return data;
  }

  /** "TORRE 1 · ROMA": el nombre se repite entre sitios, el alias lo distingue. */
  equipmentLabel(item: any): string {
    if (!item) return '';
    const place = this.placeById[item.place];
    const alias = place ? (place.alias || place.name) : '';
    return [item.name ?? item.code, alias, item.responsible].filter(v => !!v).join(' · ');
  }

  /** Índice de ubicaciones (evita un .find por opción en cada detección de cambios). */
  private get placeById(): Record<string, any> {
    if (!this._placeById) this._placeById = Object.fromEntries(((this.data?.places ?? []) as any[]).map((p: any) => [p.id, p]));
    return this._placeById;
  }
  private _placeById: Record<string, any> | null = null;

  /**@description Funcion para actulizar equipmento */
  async update(){
    try{
      const equipmentId = this.formGroup.value['equipment'];
      if (!equipmentId || !this.equipmentList.some((e: any) => e.id === equipmentId)) {
        await this.customMessageService.open({ title: '¡Cuidado!', type:'neutral', message: 'Elige un equipo del catálogo.', dismissBtn: true});
        return;
      }

      if(!this.formGroup.valid){
        await this.customMessageService.open({title: 'Cuidado', type: 'warning', message: 'Es necesario completar todos los campos.', dismissBtn: true})
        return;
      }
  
      let data = JSON.parse(JSON.stringify(this.record));
      log.debug('data', data);
      
      for (const key of Object.keys(this.formGroup.value)) {
        data[key] = this.formGroup.value[key];
      }
      
      data['operationType'] = 'single'
      this.normalizeDates(data);
      log.debug(data)
      log.debug('formGroup', this.formGroup.value)

      if(data.tagRfid.length != 24){
        await this.customMessageService.open({title: 'Cuidado', type: 'warning', message: 'La etiqueta RFID debe tener una longitud de 24 caracteres.', dismissBtn: true})
        return;
      }
  
      log.debug('record', this.record);
      log.debug('data', data);

      this.customMessageService.open({title: 'Espere', type: 'success', message: 'Guardando equipmento', loading: true });
      let apiResponse = await this.inventoryEquipmentsService.update(data);
      this.customMessageService.close();
      if (apiResponse.state == 'success') {
        await this.customMessageService.open({title: '¡Hecho!', type: 'success', message: apiResponse['message'], duration: 2000 });
        this.dialogRef.close(apiResponse.data);
      }else{
        await this.customMessageService.open({title: 'Error', type: 'error', message: apiResponse['message'], dismissBtn: true });
      }

    }catch(err){
      this.customMessageService.close();
      await this.customMessageService.open({title: 'Error', type: 'error', message: err, dismissBtn: true });
    }



  }

  /**@description Funcion para crear equipmento */
  async create(){
    try {
      const equipmentId = this.formGroup.value['equipment'];
      if (!equipmentId || !this.equipmentList.some((e: any) => e.id === equipmentId)) {
        await this.customMessageService.open({ title: '¡Cuidado!', type:'neutral', message: 'Elige un equipo del catálogo.', dismissBtn: true});
        return;
      }
      
      if(!this.formGroup.valid){
        await this.customMessageService.open({title: 'Cuidado', type: 'warning', message: 'Es necesario completar todos los campos.', dismissBtn: true})
        return;
      }
  
      let data = JSON.parse(JSON.stringify(this.formGroup.value));
      data['status'] = 'Activo'
      data['operationType'] = 'single'
      this.normalizeDates(data);
      log.debug('data after',data)

      if(data.tagRfid.length != 24){
        await this.customMessageService.open({title: 'Cuidado', type: 'warning', message: 'La etiqueta RFID debe tener una longitud de 24 caracteres.', dismissBtn: true})
        return;
      }
      
      this.customMessageService.open({title: 'Espere', type: 'success', message: 'Guardando equipmento', loading: true });
      let apiResponse = await this.inventoryEquipmentsService.create(data);
      this.customMessageService.close();
      if (apiResponse.state == 'success') {
        await this.customMessageService.open({title: '¡Hecho!', type: 'success', message: apiResponse['message'], duration: 2000 });
        this.dialogRef.close(apiResponse.data);
      }else{
        await this.customMessageService.open({title: 'Error', type: 'error', message: apiResponse['message'], dismissBtn: true });
      }
    }catch(err){
      this.customMessageService.close();
      await this.customMessageService.open({title: 'Error', type: 'error', message: err, dismissBtn: true });
    }
  }

}
